import { ExportFileType } from '@common/ts/enums';
import { toast } from 'react-toastify';
import downloadFile from './downloadFile';
import { ConversationView } from '@/enums';
import { ExportConfig } from '@/types';

/**
 * Handles common error logic for exports
 */
const handleExportError = (errorMessage: string) => {
  toast.error(errorMessage);
};

/**
 * Gets file type parameter for API calls (undefined for CSV as it's default)
 */
const getOptionalFileType = (type: ExportFileType) =>
  type === ExportFileType.CSV ? undefined : type;

/**
 * Unified utility for exporting conversation data
 */
const exportConversation = async ({
  type,
  projectId,
  conversationId,
  filterId,
  fileType,
  transcriptsView,
  mutations,
  translations,
}: ExportConfig): Promise<void> => {
  try {
    let result;
    let filename;
    let successMessage;

    switch (type) {
      case ConversationView.CONVERSATION_OVERVIEW:
        result = await mutations.overview({
          projectId,
          filterId,
          fileType: getOptionalFileType(fileType),
          transcriptsView,
        });
        filename = `conversation_overview.${fileType}`;
        successMessage = translations.overview;
        break;

      case ConversationView.CONVERSATION_DETAILS:
        result = await mutations.details({
          projectId,
          filterId,
          fileType: getOptionalFileType(fileType),
          transcriptsView,
        });
        filename = `conversation_details.${fileType}`;
        successMessage = translations.details;
        break;

      case ConversationView.SINGLE_CONVERSATION:
        result = await mutations.single({
          conversationId,
          fileType: getOptionalFileType(fileType),
        });
        filename = `tellet_conversation_${conversationId}.${fileType}`;
        successMessage = translations.single;
        break;

      case ConversationView.FULL_ANSWERS_TRANSCRIPT:
        result = await mutations.fullAnswersTranscript({
          projectId,
          filterId,
          fileType: getOptionalFileType(fileType),
          transcriptsView,
        });
        filename = `full_answers_transcript.${fileType}`;
        successMessage = translations.fullAnswersTranscript;
        break;

      default:
        throw new Error('Invalid export type');
    }

    // Download the file and show success message
    downloadFile(result.data, filename);
    toast.success(successMessage);

    return await Promise.resolve();
  } catch (error) {
    handleExportError(translations.error);
    return Promise.reject(error);
  }
};

export default exportConversation;
